// Notes, in Business (My work) and Personal (My stuff).
//
// Decision 90 v2 (Carla, 2026-10-12) REPLACED the Block J list-beside-the-note layout:
//   - the page you land on is the Journal mockup's "Your entries" section and nothing above it
//     (Notes/NotesBrowse.jsx): chips with counts and Favourites, search, "Linked to", the month
//     navigator, Journal-style cards in a grid or a list, load more after 24, "Shared with me";
//   - "+ New note" opens a chooser (Notes/NewNoteChooser.jsx): Blank note first, then the templates;
//     started from a client, project or task (?note_new=), the link is pre-filled;
//   - the editor is its own view, full screen on a phone, with "← Notes" back on every device; the
//     address is /notes/<id> (/notes/new for a note not yet saved), so Back, reload and links work;
//   - "Linked to" (Notes/LinkedTo.jsx): a client, a project and/or a task, checked by the server;
//   - a NEW note is a draft until it has a title or some content: nothing is sent before that, and a
//     new note left with no title and no content is discarded (and its link removed from the page it
//     was inside), never saved as "Untitled". A note sent early for a picture and left without one is
//     deleted again;
//   - no bare "+": "Add page inside" is in a card's ⋯ menu and in the editor's bar.
// Kept from Block J and decisions 1 and 4: blocks, pages inside pages with breadcrumbs, favourites,
// the cover colour, Share with family (Personal only), "Shared with me", pictures on the file store,
// the 1 MB sentence beside Save, the deep link 'note', deletes behind Undo, the failure states.
import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useViewTop } from "../ui/revealForm";
import BlockEditor, { textToBlocks, blocksToText, newNoteId } from "./Notes/BlockEditor";
import { templatesFor } from "./Notes/noteTemplates";
import { NOTE_COVERS, coverName } from "./Notes/noteCovers";
import ShareWithFamily from "./Notes/ShareWithFamily";
import SharedNote from "./Notes/SharedNote";
import NotesBrowse from "./Notes/NotesBrowse";
import NewNoteChooser from "./Notes/NewNoteChooser";
import LinkedTo, { LINK_KINDS } from "./Notes/LinkedTo";
import CategoryManager, { ColourPicker, Dot, DEFAULT_COLOUR, chipColour } from "./Notes/CategoryManager";
import {
  noteHasContent, noteTitle, categoryChips, chipKeyOf, editedAt, pathForNote, noteIdFromPath, NOTES_PATH,
  linkFromIntent,
} from "./Notes/noteContent";
import { api } from "../api/client";
import { describeWriteFailure } from "../utils/writeFailure";
import { showErrorToast } from "../utils/toast";
import { messageFromError } from "../utils/apiError";
import { formatDateTime } from "../utils/formatting";
import { useCurrentUser } from "../contexts/CurrentUserContext";
import { useDeepLinkItem, useDeepLinkTarget, sayItemGone, clearPendingDeepLink } from "../utils/deepLink";
import {
  PageLayout, PageHeader, Button, Chip, ChipGroup, Modal, ConfirmDialog, Popover, Input,
  ErrorState, Banner, useDelayedDelete, useBreakpoint, v,
} from "../ui";
import { TOKENS } from "../ui/tokens";
import "./Notes/notes.css";

const LINK_FIELDS = ['contactId', 'projectId', 'taskId'];

// The layout the server sent, or the empty one.
const layoutFrom = (l) => ({ order: Array.isArray(l?.order) ? l.order : [], hidden: Array.isArray(l?.hidden) ? l.hidden : [] });
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const shortWhen = (iso) => formatDateTime(iso, undefined, { year: undefined });
const sameLink = (a, b) => (a === null || a === undefined || a === '' ? null : String(a)) === (b === null || b === undefined || b === '' ? null : String(b));

// The cover: one of the kit's pastels, chosen by NAME (the server accepts only these names).
function CoverPicker({ value, onChange }) {
  return (
    <div className="nt-swatches nt-cover-pick" role="radiogroup" aria-label="Cover colour">
      {NOTE_COVERS.map(([token, name]) => (
        <button key={token} type="button" role="radio" aria-checked={value === token} aria-label={name}
          className="nt-swatch ui-hit" style={{ '--nt-dot': v(token) }} onClick={() => onChange(token)} />
      ))}
    </div>
  );
}

// The note and every page inside it, at any depth (ids).
function subtreeIds(rootId, notes) {
  const ids = new Set([rootId]);
  for (let grew = true; grew;) {
    grew = false;
    for (const n of notes) if (n.parentId && ids.has(n.parentId) && !ids.has(n.id)) { ids.add(n.id); grew = true; }
  }
  return ids;
}

const linkLists = (contacts, projects, tasks) => ({
  contacts: (contacts || []).map((c) => ({ id: String(c.id), label: String(c.name || c.company || c.email || 'Unnamed').trim() })),
  projects: (projects || []).map((p) => ({ id: String(p.id), label: String(p.name || 'Untitled project').trim() })),
  tasks: (tasks || []).filter((t) => t && !t.isBirthday && /^\d+$/.test(String(t.id))).map((t) => ({ id: String(t.id), label: String(t.title || 'Untitled task').trim() })),
});

function Notes({ quickNotes, setQuickNotes, noteCategories, setNoteCategories }) {
  const phone = useBreakpoint() === 'phone';
  const { workspaces, activeWorkspaceId } = useCurrentUser() || {};
  const personal = ((workspaces || []).find(w => w.id === activeWorkspaceId) || {}).type === 'personal';

  const [openId, setOpenId] = useState(null);
  // An editor that replaces the page opens at its top, not at the list's scroll (2026-10-21).
  useViewTop(openId);
  const [load, setLoad] = useState({ status: 'loading', error: null, catError: null });
  const [save, setSave] = useState({ id: null, status: 'idle', message: null });
  // Carla's item 4 (2026-10-21): every note whose last save FAILED, id -> the words for why. Kept
  // past closing the editor, so a save that failed on the way out (the firewall's 403 on a note
  // holding <script> or SQL-looking text, a 5xx, the network) is said on the list, and said again
  // when the note is reopened, instead of the list showing unsaved words as if they were stored.
  // Cleared only by a save of that note that lands. Never retried in a loop: the next real change
  // sends the next save.
  const [unsaved, setUnsaved] = useState({});
  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
  const [showManageCats, setShowManageCats] = useState(false);
  // Categories (the manager, 2026-10-12): her rows are App's noteCategories; the order and the deleted
  // built-ins are catLayout; the note counts are the server's, fetched again when the manager opens.
  const [catLayout, setCatLayout] = useState({ order: [], hidden: [] });
  const [catCounts, setCatCounts] = useState({ counts: null, error: null });
  const [editorCat, setEditorCat] = useState(null);   // null | { name, colour, busy, failure }
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [coverOpen, setCoverOpen] = useState(false);
  const coverRef = useRef(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shared, setShared] = useState({ status: 'idle', notes: [], error: null });
  const [sharedOpenId, setSharedOpenId] = useState(null);
  const [chooser, setChooser] = useState({ open: false, link: null });
  const [links, setLinks] = useState({ status: 'loading', error: null, contacts: [], projects: [], tasks: [] });
  const [pageRequest, setPageRequest] = useState(0);
  const [revs, setRevs] = useState({});   // a note changed from outside its open editor: remount it

  // Notes made on this page that the server does not have yet (a new note is a draft until it has a
  // title or some content; a create that failed stays here and is sent again on the next save).
  const [drafts, setDraftsState] = useState([]);
  const draftsRef = useRef([]);
  const setDrafts = useCallback((fn) => { draftsRef.current = fn(draftsRef.current); setDraftsState(draftsRef.current); }, []);
  const isDraft = (id) => draftsRef.current.some((n) => n.id === id);
  const fresh = useRef(new Set());          // made in this visit: discarded if left empty
  const focusTitle = useRef(null);          // the new note whose title gets the cursor
  const lastSaved = useRef(new Map());      // id -> the links the server has, so only a change is sent
  const latest = useRef(new Map());         // id -> the newest version of each note seen here
  const chain = useRef(Promise.resolve());
  const saveTimer = useRef(null);
  const pendingSave = useRef(null);

  const allNotes = useMemo(() => {
    const ids = new Set(drafts.map((n) => n.id));
    return [...drafts, ...(quickNotes || []).filter((n) => n && !ids.has(n.id))];
  }, [drafts, quickNotes]);
  useEffect(() => { for (const n of allNotes) latest.current.set(n.id, n); }, [allNotes]);
  const find = (id) => latest.current.get(id) || allNotes.find((n) => n.id === id) || null;

  // One write for a note, wherever it lives (a draft here, or App's list).
  const updateNote = useCallback((note) => {
    latest.current.set(note.id, note);
    if (draftsRef.current.some((n) => n.id === note.id)) setDrafts((prev) => prev.map((n) => (n.id === note.id ? { ...n, ...note } : n)));
    else setQuickNotes((prev) => prev.map((n) => (n.id === note.id ? { ...n, ...note } : n)));
  }, [setDrafts, setQuickNotes]);

  // ── Load (this page's own, so a failure is said here rather than read as "no notes") ─────
  const loadAll = useCallback(async () => {
    setLoad(l => ({ ...l, status: 'loading', error: null, catError: null }));
    const [notesRes, catsRes] = await Promise.allSettled([
      api('/api/notes').then(r => r.json()),
      api('/api/note-categories').then(r => r.json()),
    ]);
    const next = { status: 'ready', error: null, catError: null };
    if (notesRes.status === 'fulfilled' && Array.isArray(notesRes.value?.notes)) {
      for (const n of notesRes.value.notes) lastSaved.current.set(n.id, { contactId: n.contactId ?? null, projectId: n.projectId ?? null, taskId: n.taskId ?? null });
      setQuickNotes(notesRes.value.notes);
    } else {
      next.status = 'failed';
      next.error = notesRes.status === 'rejected'
        ? messageFromError(notesRes.reason, 'Your notes did not load.')
        : 'Your notes did not load: Aidey sent back something it could not read.';
    }
    if (catsRes.status === 'fulfilled' && Array.isArray(catsRes.value?.categories)) {
      setNoteCategories(catsRes.value.categories);
      setCatLayout(layoutFrom(catsRes.value.layout));
      setCatCounts({ counts: catsRes.value.counts || null, error: null });
    } else {
      next.catError = catsRes.status === 'rejected'
        ? messageFromError(catsRes.reason, 'Your categories did not load.')
        : 'Your categories did not load.';
    }
    setLoad(next);
  }, [setQuickNotes, setNoteCategories]);
  useEffect(() => { loadAll(); }, [loadAll]);

  // What a note can be linked to, and the names its pills show.
  const loadLinks = useCallback(async () => {
    setLinks((l) => ({ ...l, status: 'loading', error: null }));
    const [c, p, t] = await Promise.allSettled([
      api('/api/contacts').then(r => r.json()),
      api('/api/projects').then(r => r.json()),
      api('/api/tasks').then(r => r.json()),
    ]);
    const failed = [c, p, t].find((r) => r.status === 'rejected');
    const lists = linkLists(c.value?.contacts, p.value?.projects, t.value?.tasks);
    setLinks({ status: failed ? 'failed' : 'ready', error: failed ? messageFromError(failed.reason, 'Your clients, projects and tasks did not load.') : null, ...lists });
  }, []);
  useEffect(() => { loadLinks(); }, [loadLinks]);
  const labelFor = useCallback((kind, id) => {
    const list = links[`${kind}s`] || [];
    const hit = list.find((o) => o.id === String(id));
    if (hit) return hit.label;
    return kind === 'contact' ? (personal ? 'a person' : 'a client') : kind === 'project' ? 'a project' : 'a task';
  }, [links, personal]);

  const loadShared = useCallback(async () => {
    setShared(s => ({ ...s, status: 'loading', error: null }));
    try {
      const data = await api('/api/notes/shared').then(r => r.json());
      if (!Array.isArray(data?.notes)) throw new Error('The notes shared with you did not load: Aidey sent back something it could not read.');
      setShared({ status: 'ready', notes: data.notes, error: null });
    } catch (err) {
      setShared({ status: 'failed', notes: [], error: messageFromError(err, 'The notes shared with you did not load.') });
    }
  }, []);
  useEffect(() => {
    if (personal) loadShared();
    else { setShared({ status: 'idle', notes: [], error: null }); setSharedOpenId(null); }
  }, [personal, loadShared]);

  // ── Saving ────────────────────────────────────────────────────────────────
  // One at a time, in order, so a save never overtakes the create it depends on. A draft with no
  // title and no content is not sent (force: a picture needs its note on the server first).
  const persist = useCallback((given, { force = false } = {}) => {
    const run = async () => {
      // The newest version seen here: a save queued behind another sends what is there now.
      const note = latest.current.get(given.id) || given;
      const creating = draftsRef.current.some((n) => n.id === note.id);
      if (creating && !force && !noteHasContent(note)) return true;
      setSave({ id: note.id, status: 'saving', message: null });
      const body = {
        title: note.title, emoji: note.emoji, blocks: note.blocks, text: note.text,
        project: note.project, categoryId: note.categoryId || null,
        parentId: note.parentId || null, favourite: !!note.favourite, cover: note.cover || null,
      };
      // Links: only what changed is sent (a link the server already has is never re-checked, so a
      // note linked to a record since deleted can still be saved).
      const had = lastSaved.current.get(note.id) || { contactId: null, projectId: null, taskId: null };
      for (const f of LINK_FIELDS) if (!sameLink(note[f], creating ? null : had[f])) body[f] = note[f] ?? null;
      try {
        const res = creating
          ? await api('/api/notes', { method: 'POST', body: JSON.stringify({ ...body, id: note.id }) })
          : await api(`/api/notes/${note.id}`, { method: 'PUT', body: JSON.stringify(body) });
        const data = await res?.json?.().catch(() => null);
        lastSaved.current.set(note.id, { contactId: note.contactId ?? null, projectId: note.projectId ?? null, taskId: note.taskId ?? null });
        const updated = data?.note?.updated;
        if (creating) {
          const now = latest.current.get(note.id) || note;
          const moved = { ...now, ...(updated ? { updated } : {}) };
          latest.current.set(note.id, moved);
          setDrafts((prev) => prev.filter((n) => n.id !== note.id));
          setQuickNotes((prev) => [moved, ...prev.filter((n) => n.id !== note.id)]);
          // The address names the note once it exists.
          if (window.location.pathname === `${NOTES_PATH}/new` && openRef.current === note.id) {
            window.history.replaceState(window.history.state, '', pathForNote(note.id));
          }
        } else if (updated) {
          setQuickNotes(prev => prev.map(n => (n.id === note.id ? { ...n, updated } : n)));
        }
        setSave({ id: note.id, status: 'saved', message: null });
        setUnsaved((u) => { if (!(note.id in u)) return u; const { [note.id]: _gone, ...rest } = u; return rest; });
        return true;
      } catch (err) {
        const tooLong = err?.status === 413;
        const message = tooLong && typeof err?.body?.error === 'string'
          ? err.body.error
          : describeWriteFailure(err, creating ? 'create that note' : 'save that note');
        setSave({ id: note.id, status: 'failed', message, tooLong });
        setUnsaved((u) => ({ ...u, [note.id]: message }));
        // The page has gone (the save was sent on the way out): the only place left to say it.
        if (!mountedRef.current) showErrorToast(`Couldn't save "${noteTitle(note)}". ${message}`);
        return false;
      }
    };
    chain.current = chain.current.then(run);
    return chain.current;
  }, [setDrafts, setQuickNotes]);

  const flushPendingSave = useCallback(() => {
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    const note = pendingSave.current;
    pendingSave.current = null;
    return note ? persist(note) : Promise.resolve(true);
  }, [persist]);

  const handleEditorChange = useCallback((updatedNote) => {
    updateNote(updatedNote);
    pendingSave.current = updatedNote;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { saveTimer.current = null; const n = pendingSave.current; pendingSave.current = null; if (n) persist(n); }, 1000);
  }, [updateNote, persist]);

  // ── Discarding a new note left empty ──────────────────────────────────────
  // Run after the editor has gone (its last words have reached the page by then): every note made in
  // this visit that has no title and no content, except the one now open, is dropped; one the server
  // has (sent for a picture that never arrived) is deleted; a page inside loses its link block.
  const discardEmpty = useCallback((keepId) => {
    const ids = [...fresh.current].filter((id) => id !== keepId);
    // Deepest first, so a page inside goes before the page it sits in is looked at.
    const depth = (id) => { let d = 0; for (let n = latest.current.get(id); n && n.parentId && d < 50; n = latest.current.get(n.parentId)) d += 1; return d; };
    ids.sort((a, b) => depth(b) - depth(a));
    for (const id of ids) {
      const note = latest.current.get(id);
      if (!note) { fresh.current.delete(id); continue; }
      if (noteHasContent(note)) continue;
      fresh.current.delete(id);
      latest.current.delete(id);
      if (isDraft(id)) setDrafts((prev) => prev.filter((n) => n.id !== id));
      else {
        setQuickNotes((prev) => prev.filter((n) => n.id !== id));
        chain.current = chain.current.then(() => api(`/api/notes/${id}`, { method: 'DELETE' }).catch(() => undefined));
      }
      const parent = note.parentId ? latest.current.get(note.parentId) : null;
      if (parent && (parent.blocks || []).some((b) => b.type === 'page' && b.pageId === id)) {
        const blocks = parent.blocks.filter((b) => !(b.type === 'page' && b.pageId === id));
        const next = { ...parent, blocks: blocks.length ? blocks : textToBlocks(''), text: blocksToText(blocks) };
        updateNote(next);
        setRevs((r) => ({ ...r, [parent.id]: (r[parent.id] || 0) + 1 }));
        persist(next);
      }
    }
  }, [setDrafts, setQuickNotes, updateNote, persist]); // eslint-disable-line react-hooks/exhaustive-deps

  const openRef = useRef(null);
  openRef.current = openId;
  const closeAfterSave = useRef(null);   // the editor's close-after-Save timer, cancelled when Notes goes away
  useEffect(() => () => { clearTimeout(closeAfterSave.current); }, []);
  const prevOpen = useRef(null);
  useEffect(() => {
    const prev = prevOpen.current;
    prevOpen.current = openId;
    if (prev && prev !== openId) {
      const t = setTimeout(() => discardEmpty(openRef.current), 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [openId, discardEmpty]);
  // Leaving the page: what was typed inside the pause is saved, and an empty new note is dropped.
  const leaveRef = useRef(null);
  leaveRef.current = () => { flushPendingSave(); setTimeout(() => discardEmpty(null), 0); };
  useEffect(() => () => { leaveRef.current(); }, []);

  // ── The address: /notes, /notes/<id>, /notes/new ───────────────────────────
  const depthNow = () => (window.history.state && Number(window.history.state.ntDepth)) || 0;
  const showNote = useCallback((id, { push = true } = {}) => {
    const path = isDraft(id) ? `${NOTES_PATH}/new` : pathForNote(id);
    try {
      // Opened from somewhere that is not a Notes address (the Dashboard's tile): Back and "← Notes"
      // come back to Notes, never to the page before it.
      if (!/^\/notes(\/|$)/.test(window.location.pathname)) window.history.pushState({ ntDepth: 0 }, '', NOTES_PATH);
      if (push) window.history.pushState({ ntDepth: depthNow() + 1 }, '', path);
      else window.history.replaceState({ ntDepth: depthNow() }, '', path);
    } catch { /* an address that cannot be written leaves the page working */ }
    setOpenId(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fromPath = useCallback(() => {
    const id = noteIdFromPath(window.location.pathname);
    return id && id !== 'new' ? id : null;
  }, []);
  // Back and Forward, and the sidebar's Notes while a note is open (App says the path changed).
  useEffect(() => {
    const onPath = () => {
      if (!/^\/notes(\/|$)/.test(window.location.pathname)) return;
      flushPendingSave();
      setSharedOpenId(null);
      setEditorCat(null);
      setCoverOpen(false);
      const id = fromPath();
      setOpenId(id && latest.current.has(id) ? id : null);
    };
    window.addEventListener('popstate', onPath);
    window.addEventListener('aidey:pathchange', onPath);
    return () => { window.removeEventListener('popstate', onPath); window.removeEventListener('aidey:pathchange', onPath); };
  }, [flushPendingSave, fromPath]);

  // Opened at /notes/<id> (a reload, a link): once the notes are in, open it or say it is gone.
  const startPath = useRef(fromPath());
  useEffect(() => {
    if (load.status !== 'ready' || !startPath.current) return;
    const id = startPath.current;
    startPath.current = null;
    if (latest.current.has(id) || (quickNotes || []).some((n) => n.id === id)) showNote(id, { push: false });
    else { sayItemGone(); try { window.history.replaceState({}, '', NOTES_PATH); } catch { /* ignore */ } }
  }, [load.status]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (window.location.pathname === `${NOTES_PATH}/new`) { try { window.history.replaceState({}, '', NOTES_PATH); } catch { /* ignore */ } }
  }, []);

  // The deep link 'note' (the Dashboard tile, search, a notification): the note in the editor view.
  useDeepLinkItem('note', {
    ready: load.status === 'ready',
    find: (id) => (quickNotes || []).find(n => String(n.id) === id),
    open: (n) => { setSharedOpenId(null); showNote(n.id, { push: false }); },
  });
  // ?note_new=contact:<id> | project:<id> | task:<id>: "+ New note" from a record, the link pre-filled.
  const [newFor, clearNewFor] = useDeepLinkTarget('note_new');
  useEffect(() => {
    if (!newFor || load.status !== 'ready') return;
    const link = linkFromIntent(newFor);
    clearNewFor();
    clearPendingDeepLink();
    setChooser({ open: true, link });
  }, [newFor, load.status, clearNewFor]);

  // ── Deleting, behind Undo (decision 7) ────────────────────────────────────
  const noteDelete = useDelayedDelete({
    send: (n, { keepalive }) => chain.current.then(() => (isDraft(n.id)
      ? undefined   // never reached the server: nothing to delete there
      : api(`/api/notes/${n.id}`, { method: 'DELETE', keepalive }))),
    onDeleted: (n) => {
      const gone = subtreeIds(n.id, [...latest.current.values()]);
      gone.forEach((id) => { fresh.current.delete(id); latest.current.delete(id); });
      setDrafts((prev) => prev.filter((x) => !gone.has(x.id)));
      setQuickNotes(prev => prev.filter(x => !gone.has(x.id)));
    },
    describe: (n) => (n.inside
      ? `Deleted "${noteTitle(n)}" and ${plural(n.inside, 'page')} inside it.`
      : `Deleted "${noteTitle(n)}".`),
    failMessage: 'That note could not be deleted.',
  });

  // ── Categories ─────────────────────────────────────────────────────────────
  // One source of truth: her rows (noteCategories) + catLayout -> allChips. The filter chips, the cards,
  // the editor's picker and Manage categories all use them, so a change shows everywhere at once.
  const [catFailure, setCatFailure] = useState({});
  const upsertRow = useCallback((row) => setNoteCategories((prev) => (prev.some((c) => c.id === row.id) ? prev.map((c) => (c.id === row.id ? row : c)) : [...prev, row])), [setNoteCategories]);
  const loadCategories = useCallback(async () => {
    setCatCounts((c) => ({ ...c, error: null }));
    try {
      const data = await api('/api/note-categories').then((r) => r.json());
      if (!Array.isArray(data?.categories)) throw new Error('Aidey sent back something it could not read.');
      setNoteCategories(data.categories);
      setCatLayout(layoutFrom(data.layout));
      setCatCounts({ counts: data.counts || null, error: null });
    } catch (err) {
      setCatCounts((c) => ({ ...c, error: messageFromError(err, 'Your categories did not load.') }));
    }
  }, [setNoteCategories]);
  function openManager() {
    setShowManageCats(true);
    setCatCounts((c) => ({ ...c, counts: null }));
    loadCategories();
  }
  async function createCategory(name, colour) {
    if (!name.trim()) return null;
    try {
      const res = await api('/api/note-categories', { method: 'POST', body: JSON.stringify({ name: name.trim(), colour }) });
      const data = await res.json();
      if (data.category) {
        upsertRow(data.category);
        const key = data.category.builtIn || `cat:${data.category.id}`;
        setCatCounts((c) => (c.counts ? { ...c, counts: { ...c.counts, [key]: c.counts[key] || 0 } } : c));
        return data.category;
      }
      return { failed: 'Could not add that category: Aidey did not return it. Nothing was added.' };
    } catch (err) {
      return { failed: describeWriteFailure(err, 'add that category') };
    }
  }
  // Rename or recolour. A built-in with no row of hers becomes one now (renamed, recoloured, or a note
  // filed under it), bound to it by the server.
  async function saveCategory(entry, patch) {
    try {
      const url = entry.catId ? `/api/note-categories/${entry.catId}` : `/api/note-categories/built-in/${entry.key}`;
      const body = entry.catId ? patch : { name: entry.name, colour: TOKENS[entry.token] || DEFAULT_COLOUR, ...patch };
      const res = await api(url, { method: 'PUT', body: JSON.stringify(body) });
      const data = await res.json();
      if (!data.category) return { failed: `Could not change ${entry.name}: Aidey did not return it. Refresh to see what was saved.` };
      upsertRow(data.category);
      return data.category;
    } catch (err) {
      return { failed: describeWriteFailure(err, `change ${entry.name}`) };
    }
  }
  // Move one category to a new place among those shown; the order is saved at once and put back if
  // the server refuses it.
  async function moveCategory(key, to) {
    const visible = chips.map((c) => c.key);
    const from = visible.indexOf(key);
    if (from < 0 || to < 0 || to >= visible.length || from === to) return null;
    const moved = [...visible];
    moved.splice(to, 0, moved.splice(from, 1)[0]);
    // A category waiting behind Undo keeps its place, so Undo puts it back where it was.
    let i = 0;
    const order = allChips.map((c) => (isCatHidden(c.key) ? c.key : moved[i++]));
    const before = catLayout;
    setCatLayout((l) => ({ ...l, order }));
    try {
      const res = await api('/api/note-categories/order', { method: 'PUT', body: JSON.stringify({ order }) });
      const data = await res.json();
      if (data?.layout) setCatLayout(layoutFrom(data.layout));
      return null;
    } catch (err) {
      setCatLayout(before);
      return describeWriteFailure(err, 'move that category');
    }
  }
  async function deleteCategory(entry, { keepalive } = {}) {
    try {
      const url = entry.catId ? `/api/note-categories/${entry.catId}` : `/api/note-categories/built-in/${entry.key}`;
      await api(url, { method: 'DELETE', keepalive });
      setCatFailure(prev => ({ ...prev, [entry.key]: undefined }));
    } catch (err) {
      setCatFailure(prev => ({ ...prev, [entry.key]: describeWriteFailure(err, `delete ${entry.name}`) }));
      throw err;
    }
  }
  const catDelete = useDelayedDelete({
    send: (c, opts) => deleteCategory(c, opts),
    onDeleted: (c) => {
      if (c.catId) setNoteCategories(prev => prev.filter(x => x.id !== c.catId));
      setCatLayout((l) => ({
        order: l.order.filter((k) => k !== c.key),
        hidden: c.builtIn ? [...new Set([...l.hidden, c.builtIn])] : l.hidden,
      }));
      if (c.catId) setQuickNotes(prev => prev.map(n => (n.categoryId === c.catId ? { ...n, categoryId: null } : n)));
      setCatCounts((s) => {
        if (!s.counts) return s;
        const { [c.key]: gone = 0, ...rest } = s.counts;
        return { ...s, counts: { ...rest, general: (rest.general || 0) + gone } };
      });
    },
    describe: (c) => `Deleted the category "${c.name}". Its notes are in General.`,
    failMessage: 'That category could not be deleted.',
  });

  const isCatHidden = catDelete.isHidden;
  const allChips = useMemo(() => categoryChips(noteCategories, catLayout), [noteCategories, catLayout]);
  const chips = useMemo(() => allChips.filter((c) => !isCatHidden(c.key)), [allChips, isCatHidden]);
  // Rows waiting behind Undo: their notes show under General until the window ends or Undo.
  const pendingCatIds = useMemo(() => new Set(allChips.filter((c) => c.catId && isCatHidden(c.key)).map((c) => c.catId)), [allChips, isCatHidden]);
  const cats = useMemo(() => (noteCategories || []).filter(c => !pendingCatIds.has(c.id)), [noteCategories, pendingCatIds]);

  // ── What is shown ─────────────────────────────────────────────────────────
  const isNoteHidden = noteDelete.isHidden;
  const visibleNotes = useMemo(() => {
    const hidden = new Set();
    allNotes.filter(n => isNoteHidden(n.id)).forEach(n => subtreeIds(n.id, allNotes).forEach(id => hidden.add(id)));
    return allNotes.filter(n => !hidden.has(n.id));
  }, [allNotes, isNoteHidden]);
  const noteById = useMemo(() => new Map(visibleNotes.map(n => [n.id, n])), [visibleNotes]);
  // The dashboard lists notes the server has, and drafts only once they have something in them.
  const browseNotes = useMemo(() => visibleNotes
    .filter((n) => !drafts.some((d) => d.id === n.id) || noteHasContent(n))
    .map((n) => (n.categoryId && pendingCatIds.has(n.categoryId) ? { ...n, categoryId: null } : n)), [visibleNotes, drafts, pendingCatIds]);

  const openNote = openId ? noteById.get(openId) || null : null;
  const sharedOpen = sharedOpenId ? shared.notes.find(n => n.id === sharedOpenId) || null : null;
  const ancestors = [];
  for (let p = openNote && noteById.get(openNote.parentId); p && ancestors.length < 50; p = noteById.get(p.parentId)) ancestors.unshift(p);

  // ── Note CRUD ──────────────────────────────────────────────────────────────
  // A new note is a DRAFT: nothing is sent until it has a title or some content.
  function createNote({ id, title = '', emoji = '', blocks, parentId = null, categoryKey = null, link = null } = {}) {
    const bl = blocks || textToBlocks('');
    const chip = categoryKey ? chips.find((c) => c.key === categoryKey) : null;
    const note = {
      id: id || newNoteId(), title, emoji, blocks: bl, text: blocksToText(bl),
      created: new Date().toISOString(), categoryId: chip?.catId || null,
      parentId, favourite: false, cover: null,
      contactId: link?.contactId ?? null, projectId: link?.projectId ?? null, taskId: link?.taskId ?? null,
    };
    flushPendingSave();
    fresh.current.add(note.id);
    focusTitle.current = note.id;
    latest.current.set(note.id, note);
    setDrafts((prev) => [note, ...prev]);
    setSharedOpenId(null);
    setEditorCat(null);
    setCoverOpen(false);
    setSave({ id: null, status: 'idle', message: null });
    showNote(note.id);
    // Sent only if it already has something in it (a template). A template goes under the person's
    // own category of its kind when she has one (her "Meetings" for Meeting notes); no category is
    // made for her: that happens only when she picks a built-in chip in the editor.
    persist(note);
  }

  // "Page inside" (the block, or "Add page inside"): the parent, now holding the link, is saved
  // first; then the new page opens as a draft.
  function createPageInside(parentNote, pageId) {
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    pendingSave.current = null;
    updateNote(parentNote);
    persist(parentNote);
    createNote({ id: pageId, parentId: parentNote.id });
  }
  // From a card's ⋯ (the parent's editor is not open): its blocks gain the link here.
  function addPageInsideFromCard(parent) {
    const pageId = newNoteId();
    const blocks = [...(parent.blocks?.length ? parent.blocks : textToBlocks(parent.text || '')), { id: Math.random().toString(36).slice(2, 10), type: 'page', html: '', pageId, checked: false, src: '', alt: '' }];
    createPageInside({ ...parent, blocks, text: blocksToText(blocks) }, pageId);
  }

  function openNoteById(id) {
    if (id === openId && !sharedOpenId) return;
    flushPendingSave();
    setEditorCat(null);
    setCoverOpen(false);
    setSave({ id: null, status: 'idle', message: null });
    setSharedOpenId(null);
    showNote(id);
  }
  function openShared(id) {
    flushPendingSave();
    setEditorCat(null);
    setCoverOpen(false);
    setOpenId(null);
    setSharedOpenId(id);
  }
  // "← Notes": back to the page, by the history it came through when there is one.
  function closeEditor() {
    flushPendingSave();
    setEditorCat(null);
    setCoverOpen(false);
    setSharedOpenId(null);
    setOpenId(null);
    const d = depthNow();
    try {
      if (d > 0 && /^\/notes\//.test(window.location.pathname)) window.history.go(-d);
      else if (window.location.pathname !== NOTES_PATH) window.history.replaceState({}, '', NOTES_PATH);
    } catch { /* ignore */ }
  }

  // ── Pictures: the private file store (decision 1) ──────────────────────────
  const openNoteRef = useRef(null);
  openNoteRef.current = openNote;
  const uploadImage = useCallback(async (file) => {
    const note = openNoteRef.current;
    if (!note) throw new Error('Open a note first.');
    await flushPendingSave();
    await chain.current;
    if (isDraft(note.id) && !(await persist(latest.current.get(note.id) || note, { force: true }))) {
      throw new Error('This note has not been saved yet, so the picture cannot be added. Save the note, then try again.');
    }
    const form = new FormData();
    form.append('kind', 'note');
    form.append('entityId', note.id);
    form.append('file', file);
    const data = await api('/api/files', { method: 'POST', body: form }).then(r => r.json());
    if (!data?.file?.id) throw new Error('That picture was not saved: Aidey did not return it. Try again.');
    return data.file.id;
  }, [flushPendingSave, persist]); // eslint-disable-line react-hooks/exhaustive-deps

  const imageLink = useCallback(async (fileId) => {
    const data = await api(`/api/files/${encodeURIComponent(fileId)}/link`).then(r => r.json());
    if (!data?.url) throw new Error('This picture could not be opened.');
    return data;
  }, []);

  const pageInfo = useCallback((id) => {
    const n = noteById.get(id);
    return n ? { title: noteTitle(n), emoji: n.emoji } : null;
  }, [noteById]);

  async function handleSave() {
    if (!openNoteRef.current) return;
    await new Promise(r => setTimeout(r, 0));
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    const note = pendingSave.current || latest.current.get(openNoteRef.current.id) || openNoteRef.current;
    pendingSave.current = null;
    if (!note) return;
    if (isDraft(note.id) && !noteHasContent(note)) {
      setSave({ id: note.id, status: 'empty', message: 'Nothing to save yet: give the note a title or write something.' });
      return;
    }
    const id = note.id;
    const ok = await persist(note);
    // Close shortly after a manual Save, but never once the page has gone (the timer used to outlive
    // Notes and send the browser BACK after she had moved on) or a different note is open.
    if (ok && openRef.current === id) {
      clearTimeout(closeAfterSave.current);
      closeAfterSave.current = setTimeout(() => { closeAfterSave.current = null; if (openRef.current === id) closeEditor(); }, 700);
    }
  }

  function handleDeleteNote(note) {
    const inside = subtreeIds(note.id, visibleNotes).size - 1;
    if (inside > 0) { setConfirmDelete({ note, inside }); return; }
    deleteNow(note, 0);
  }
  function deleteNow(note, inside) {
    flushPendingSave();
    if (openRef.current && subtreeIds(note.id, visibleNotes).has(openRef.current)) closeEditor();
    setEditorCat(null);
    setCoverOpen(false);
    subtreeIds(note.id, visibleNotes).forEach((id) => fresh.current.delete(id));
    noteDelete.remove({ ...note, inside });
  }

  // Filing under a chip: its own category, or (a built-in she has none for) one made now.
  async function fileUnder(noteId, chip) {
    const target = () => latest.current.get(noteId);
    if (!chip || (chip.key === 'general' && !chip.catId)) { const n = target(); if (n) handleEditorChange({ ...n, categoryId: null }); return; }
    if (chip.catId) { const n = target(); if (n) handleEditorChange({ ...n, categoryId: chip.catId }); return; }
    const made = await saveCategory(chip, {});
    if (!made || made.failed) { setEditorCat({ name: chip.name, colour: DEFAULT_COLOUR, busy: false, failure: made?.failed || 'That category could not be made.' }); return; }
    const n = target();
    if (n) handleEditorChange({ ...n, categoryId: made.id });
  }

  async function addEditorCategory() {
    if (!editorCat?.name?.trim() || editorCat.busy) return;
    setEditorCat(c => ({ ...c, busy: true, failure: null }));
    const cat = await createCategory(editorCat.name.trim(), editorCat.colour);
    if (!cat || cat.failed) {
      setEditorCat(c => (c ? { ...c, busy: false, failure: cat?.failed || 'Give the category a name.' } : c));
      return;
    }
    setEditorCat(null);
    const n = latest.current.get(openNote.id) || openNote;
    handleEditorChange({ ...n, categoryId: cat.id });
  }

  function toggleFavourite(note) {
    const n = { ...(latest.current.get(note.id) || note), favourite: !note.favourite };
    updateNote(n);
    persist(n);
  }

  const templates = templatesFor(personal);
  const startNew = (link = null) => setChooser({ open: true, link });
  const chooserLink = chooser.link;
  const chooserLinkLabel = chooserLink
    ? (chooserLink.contactId ? labelFor('contact', chooserLink.contactId) : chooserLink.projectId ? labelFor('project', chooserLink.projectId) : labelFor('task', chooserLink.taskId))
    : null;

  const linkOf = useCallback((n) => {
    for (const k of LINK_KINDS) {
      const id = n[k.field];
      if (id !== null && id !== undefined && id !== '') return { kind: k.kind, icon: k.icon, label: labelFor(k.kind, id) };
    }
    return null;
  }, [labelFor]);
  const linkOptions = useMemo(() => {
    const out = [];
    const seen = new Set();
    for (const n of browseNotes) {
      if (n.contactId && !seen.has(`contact:${n.contactId}`)) { seen.add(`contact:${n.contactId}`); out.push({ value: `contact:${n.contactId}`, label: `👤 ${labelFor('contact', n.contactId)}` }); }
      if (n.projectId !== null && n.projectId !== undefined && !seen.has(`project:${n.projectId}`)) { seen.add(`project:${n.projectId}`); out.push({ value: `project:${n.projectId}`, label: `📁 ${labelFor('project', n.projectId)}` }); }
    }
    return out.sort((a, b) => a.label.localeCompare(b.label));
  }, [browseNotes, labelFor]);

  const actionsFor = (n) => [
    { label: 'Open', icon: '📄', onSelect: () => openNoteById(n.id) },
    { label: 'Add page inside', icon: '📑', onSelect: () => addPageInsideFromCard(latest.current.get(n.id) || n) },
    { label: n.favourite ? 'Remove from favourites' : 'Add to favourites', icon: n.favourite ? '☆' : '⭐', onSelect: () => toggleFavourite(n) },
    { label: 'Delete', icon: '🗑', danger: true, onSelect: () => handleDeleteNote(n) },
  ];

  const ownCount = browseNotes.filter((n) => !n.parentId || !noteById.has(n.parentId)).length;
  const subline = load.status === 'loading' && !quickNotes.length
    ? 'Loading your notes…'
    : [plural(ownCount, 'note'), cats.length ? plural(cats.length, 'category', 'categories') : null].filter(Boolean).join(' · ');

  // ── The open note ──────────────────────────────────────────────────────────
  let editor = null;
  if (openNote) {
    const chipKey = chipKeyOf(openNote, chips);
    const mine = save.id === openNote.id ? save
      : unsaved[openNote.id] ? { status: 'failed', message: unsaved[openNote.id] } : { status: 'idle' };
    const draftEmpty = isDraft(openNote.id) && !noteHasContent(openNote);
    const meta = (
      <div className="nt-meta">
        <LinkedTo note={openNote} options={links} onRetry={loadLinks} labelFor={labelFor} personal={personal}
          onChange={(patch) => handleEditorChange({ ...(latest.current.get(openNote.id) || openNote), ...patch })} />
        <div className="nt-meta__chips">
          <ChipGroup label="Category" value={chipKey || ''} scroll
            onChange={(key) => fileUnder(openNote.id, chips.find((c) => c.key === key))}
            options={chips.map(c => ({ value: c.key, label: c.name, icon: <Dot colour={chipColour(c)} /> }))} />
          {!editorCat && (
            <Chip onClick={() => setEditorCat({ name: '', colour: DEFAULT_COLOUR, busy: false, failure: null })}>+ New category</Chip>
          )}
        </div>
        {editorCat && (
          <div className="nt-newcat">
            <Input aria-label="New category name" placeholder="Category name" value={editorCat.name} autoFocus
              onChange={e => setEditorCat(c => ({ ...c, name: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') addEditorCategory(); if (e.key === 'Escape') setEditorCat(null); }} />
            <ColourPicker value={editorCat.colour} onChange={col => setEditorCat(c => ({ ...c, colour: col }))} />
            <div className="nt-newcat__actions">
              <Button size="sm" variant="tertiary" onClick={() => setEditorCat(null)}>Cancel</Button>
              <Button size="sm" variant="confirm" onClick={addEditorCategory} disabled={!editorCat.name.trim() || editorCat.busy}>Add category</Button>
            </div>
            {editorCat.failure && <p className="nt-failed" role="alert" data-testid="editor-cat-failed">{editorCat.failure}</p>}
          </div>
        )}
        {editedAt(openNote) && !isDraft(openNote.id) && <span className="nt-meta__edited">Edited {shortWhen(editedAt(openNote))}</span>}
      </div>
    );
    const fav = !!openNote.favourite;
    const cat = cats.find((c) => c.id === openNote.categoryId);
    const coverColour = coverName(openNote.cover) ? v(openNote.cover) : (cat?.colour || v('pastel-pink'));
    editor = (
      <div className="nt-editor">
        <div className="nt-bar">
          <nav className="nt-crumbs" aria-label="Breadcrumb">
            <button type="button" className="nt-crumbs__back" onClick={closeEditor}>← Notes</button>
            {ancestors.map(a => (
              <span key={a.id} className="nt-crumbs__step">
                <span className="nt-crumbs__sep" aria-hidden="true">/</span>
                <button type="button" className="nt-crumbs__back nt-crumbs__up" onClick={() => openNoteById(a.id)}
                  aria-label={`${a.emoji ? `${a.emoji} ` : ''}${noteTitle(a)}`} title={noteTitle(a)}>
                  {a.emoji ? `${a.emoji} ` : ''}{noteTitle(a)}
                </button>
              </span>
            ))}
            <span className="nt-crumbs__sep" aria-hidden="true">/</span>
            <span className="nt-crumbs__here" aria-current="page">{openNote.emoji ? `${openNote.emoji} ` : ''}{noteTitle(openNote)}</span>
          </nav>
          <div className="nt-bar__actions">
            <span className="nt-status" role="status">
              {mine.status === 'saving' ? 'Saving…' : mine.status === 'saved' ? 'Saved' : mine.status === 'failed' ? "Couldn't save, your text is kept here" : draftEmpty ? 'Kept once it has a title or some writing' : ''}
            </span>
            <Button variant="secondary" size="sm" aria-pressed={fav} className={fav ? 'nt-fav nt-fav--on' : 'nt-fav'}
              onClick={() => handleEditorChange({ ...(latest.current.get(openNote.id) || openNote), favourite: !fav })}>
              <span aria-hidden="true">{fav ? '⭐' : '☆'}</span>{fav ? 'Favourited' : 'Favourite'}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setPageRequest((n) => n + 1)}>
              <span aria-hidden="true">📑</span>Add page inside
            </Button>
            {personal && (
              <Button variant="secondary" size="sm" onClick={() => setShareOpen(true)}>Share with family</Button>
            )}
            <Button variant="confirm" size="sm" onClick={handleSave} disabled={mine.status === 'saving'}>Save</Button>
            <Button variant="destructive" size="sm" onClick={() => handleDeleteNote(openNote)}>
              <span aria-hidden="true">🗑</span>Delete
            </Button>
          </div>
        </div>
        {mine.status === 'failed' && (
          <p className="nt-failed nt-failed--bar" role="alert" data-testid="note-save-failed">
            {mine.message} {mine.tooLong ? 'Your writing is still here.' : 'Your writing is still here; press Save to try again.'}
          </p>
        )}
        {mine.status === 'empty' && <p className="nt-failed nt-failed--bar" role="alert" data-testid="note-save-empty">{mine.message}</p>}
        <article className="nt-sheet">
          <button ref={coverRef} type="button" className="nt-cover" style={{ '--nt-cover': coverColour }}
            aria-label="Change cover colour" aria-expanded={coverOpen} aria-haspopup="dialog"
            onClick={() => setCoverOpen(o => !o)} />
          <Popover anchorRef={coverRef} open={coverOpen} onClose={() => setCoverOpen(false)} title="Cover colour" align="left">
            <div className="nt-cover-pop">
              <CoverPicker value={openNote.cover || null}
                onChange={(token) => { handleEditorChange({ ...(latest.current.get(openNote.id) || openNote), cover: token }); setCoverOpen(false); }} />
              {openNote.cover && (
                <Button size="sm" variant="tertiary" onClick={() => { handleEditorChange({ ...(latest.current.get(openNote.id) || openNote), cover: null }); setCoverOpen(false); }}>
                  Use the category colour
                </Button>
              )}
            </div>
          </Popover>
          <div className="nt-sheet__body">
            <BlockEditor key={`${openNote.id}:${revs[openNote.id] || 0}`} note={openNote} onChange={handleEditorChange} meta={meta}
              uploadImage={uploadImage} imageLink={imageLink}
              onCreatePage={createPageInside} pageInfo={pageInfo} onOpenPage={openNoteById}
              autoFocusTitle={focusTitle.current === openNote.id} pageRequest={pageRequest} />
          </div>
        </article>
        <p className="nt-tip">Tip: type <b>/</b> on an empty line to add headings, to-dos, callouts, a page inside and more. Enter makes a new line; Backspace on an empty line removes it. Each block's ⋮⋮ menu moves, copies or deletes it.</p>
      </div>
    );
  } else if (sharedOpen) {
    editor = (
      <SharedNote note={sharedOpen} phone={phone} onClose={closeEditor} imageLink={imageLink}
        pageInfo={(id) => { const n = shared.notes.find(x => x.id === id); return n ? { title: noteTitle(n), emoji: n.emoji } : null; }}
        onOpenPage={openShared} />
    );
  }

  // ── Manage categories ──────────────────────────────────────────────────────
  const manage = (
    <CategoryManager open={showManageCats} onClose={() => setShowManageCats(false)} phone={phone}
      entries={chips} counts={catCounts.counts} countsError={catCounts.error} onRetryCounts={loadCategories}
      onAdd={(name, colour) => createCategory(name, colour)}
      onSave={async (entry, patch) => { const r = await saveCategory(entry, patch); return r?.failed || null; }}
      onMove={moveCategory}
      onDelete={(entry) => catDelete.remove({ ...entry, id: entry.key })}
      failures={catFailure} />
  );

  const failedOutright = load.status === 'failed' && !visibleNotes.length;

  return (
    <PageLayout data-page="notes">
      {!editor && (
        <PageHeader
          eyebrow={{ label: personal ? 'My stuff' : 'My work', color: 'group-my-work' }}
          title="Notes"
          subline={subline}
          primary={<Button variant="create" className="ui-addtask" data-green-create onClick={() => startNew()} data-testid="notes-new">+ New note</Button>}
          secondary={[{ label: 'Manage categories', onClick: openManager }]}
        />
      )}

      {editor ? (
        <div className="nt-editview">{editor}</div>
      ) : failedOutright ? (
        <ErrorState title="Your notes didn't load" message={load.error} onRetry={loadAll} />
      ) : (
        <>
          {load.status === 'failed' && (
            <Banner tone="problem" action={<Button size="sm" variant="secondary" onClick={loadAll}>Try again</Button>}>
              These may be out of date. {load.error}
            </Banner>
          )}
          {Object.keys(unsaved).filter((id) => find(id)).map((id) => (
            <div key={id} data-testid="notes-unsaved">
              <Banner tone="problem" action={<Button size="sm" variant="secondary" onClick={() => openNoteById(id)}>Open it</Button>}>
                Couldn't save "{noteTitle(find(id))}", your text is kept here. {unsaved[id]} Open it and press Save to try again.
              </Banner>
            </div>
          ))}
          {load.catError && (
            <Banner tone="problem" action={<Button size="sm" variant="secondary" onClick={loadAll}>Try again</Button>}>
              Your categories did not load. {load.catError}
            </Banner>
          )}
          <NotesBrowse notes={browseNotes} categories={cats} chips={chips} linkOf={linkOf} linkOptions={linkOptions}
            loading={load.status === 'loading'} onOpen={openNoteById} onNew={() => startNew()} actionsFor={actionsFor}
            shared={personal ? shared.notes : null} sharedState={shared.status} sharedError={shared.error} onRetryShared={loadShared}
            onOpenShared={openShared} />
        </>
      )}
      {manage}
      <NewNoteChooser open={chooser.open} onClose={() => setChooser({ open: false, link: null })} templates={templates} linkLabel={chooserLinkLabel}
        onBlank={() => { const link = chooser.link; setChooser({ open: false, link: null }); createNote({ link }); }}
        onTemplate={(t) => { const link = chooser.link; setChooser({ open: false, link: null }); createNote({ title: t.title, emoji: t.emoji, blocks: t.blocks(), categoryKey: t.category, link }); }} />
      <ConfirmDialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}
        title={confirmDelete ? `Delete "${noteTitle(confirmDelete.note)}"?` : 'Delete this page?'}
        message={confirmDelete
          ? `"${noteTitle(confirmDelete.note)}" and the ${plural(confirmDelete.inside, 'page')} inside it will be deleted. You can undo it for a few seconds afterwards.`
          : ''}
        confirmLabel="Delete"
        onConfirm={() => { const c = confirmDelete; if (c) deleteNow(c.note, c.inside); }} />
      {personal && openNote && (
        <ShareWithFamily open={shareOpen} onClose={() => setShareOpen(false)} note={openNote}
          ensureSaved={async () => {
            await flushPendingSave();
            await chain.current;
            return !isDraft(openNote.id) || persist(latest.current.get(openNote.id) || openNote, { force: true });
          }} />
      )}
    </PageLayout>
  );
}

export default Notes;
